const MOUSE_BUTTON = Object.freeze({
    LEFT: 0,
    RIGHT: 2
});

/**
 * The main class for interacting with Esri ArcGIS JS API. This class loads the ArcGIS modules, creates map, view and layers and provides map navigation
 */
class MapperEsri {
    constructor() {
        Object.assign(
            this,
            FeatureLayerHelper,
            GraphicsLayerHelper,
            GraphicHelper,
            CustomPopupHelper
        );
        this.modules = {};
        this.isViewInitialized = false;
        this.selectionLayer;
        this.sketchTool;
        this.selectionId = 0;
        this.popupContainer;
        this.popupContainerOffset;
        this.selectionLayerId = 'selectionLayerId';

        // Default map options
        this.scrollWheel = true;
        this.layers = [];
        this.basemap1 = 'streets-navigation-vector';
        this.basemap2 = 'hybrid';
        this.canToggleBasemap = true;
        this.canOpenFullscreenMap = false;
        this.zoom = 1;
        this.latitude = 0;
        this.longitude = 0;
        this.disableDefaultUI = false;
        this.showPopup = true;
        this.focusOnPopup = false;
        this.canZoomToPopup = false;
        this.tooltipDelay = 0;
        this.isTooltipVisible = false;
        this.mouseEventHandlers = {};
        this.showSelectionToolbar = false;
        this.selectionCreatedHandler = {};
    }

    /**
     * @callback selectionCreatedHandler
     * @param {Object} graphic
     * @param {boolean} isCompleted
     */

    /**
     * Initialize the map view using the given properties. This function must be called before the map can be used
     * @param mapDivId Id of the map's container HTML element
     * @param options optional settings for configuraing the map's initial look and feel. The following options are supported:
     * @param {object} options.center: a `latitude, longitide` key/value pair indicating map center
     * @param {string} options.basemap1Id: name of the map's primary basemaps. See https://developers.arcgis.com/javascript/latest/api-reference/esri-Map.html#basemap for all available basemap options
     * @param {string} options.basemap2Id: name of the map's secondary basemaps.
     * @param {boolean} options.canToggleBasemap indicate if the map will show a button for toggling between the 2 basemaps
     * @param {boolean} options.canOpenFullscreenMap indicate if the map will show a fullscreen button
     * @param {[{string, string, featureLayerOptions}]} options.layers: An array of { id, type, options } key-value pairs representing the map's operational layer(s). The first layer will be rendered right above the basemap, the last layer will be rendered at the top.
     * `type` is either "GarphicsLayer" (default) or "FeatureLayer". See the difference explained in the first table here: https://developers.arcgis.com/javascript/latest/api-reference/esri-layers-Layer.html.
     * When creating a FeatureLayer, addition properties need to be passed via "options". See https://developers.arcgis.com/javascript/latest/api-reference/esri-layers-FeatureLayer.html#properties-summary for details.
     * @param {number} options.zoom: initial zoom level from 0 to 20 (smaller number will zoom to smaller area)
     * @param {boolean} options.disableDefaultUI: indicate if the map UIs (zoom, basemap toggle) will be disabled
     * @param {boolean} options.showPopup: indicate if a popup will be displayed when user clicks on a map feature
     * @param {boolean} options.focusOnPopup: indicate if the map view should reposition to focus on the popup
     * @param {boolean} option.scrollWheel: indicate if zooming using a mouse scroll wheel should be disabled
     * @param {boolean} options.canZoomToPopup: indicate if popup will provide a zoom icon for zooming in to the popup feature(s)
     * @param {number} options.tooltipDelay: The delay, in milliseconds before showing a tooltip.
     * @param {showSelectionToolbar} options.showSelectionToolbar: indicates if the map view should show a selection toolbar.
     * @param {selectionCreatedHandler} selectionCreatedHandler
     * @returns {Promise<boolean|null>} true if successfully initialized, or null for rejected promise
     */
    async initializeMapView(mapDivId, options, version) {
        let modules = [
            ['esri/Map', 'Map'],
            ['esri/views/MapView', 'MapView'],
            ['esri/widgets/BasemapToggle', 'BasemapToggle'],
            ['esri/widgets/Fullscreen', 'Fullscreen'],
            ['esri/widgets/Sketch', 'Sketch'],
            ['esri/layers/GraphicsLayer', 'GraphicsLayer'],
            ['esri/layers/FeatureLayer', 'FeatureLayer'],
            ['esri/symbols/CIMSymbol', 'CIMSymbol'],
            ['esri/Graphic', 'Graphic'],
            ['esri/geometry/Point', 'Point'],
            ['esri/geometry/Circle', 'Circle'],
            ['esri/geometry/Polygon', 'Polygon'],
            ['esri/geometry/Extent', 'Extent'],
            ['esri/geometry/geometryEngineAsync', 'geometryEngineAsync'],
            ['esri/PopupTemplate', 'PopupTemplate'],
            ['esri/geometry/support/webMercatorUtils', 'webMercatorUtils'],
            ['dojo/dom-construct', 'domConstruct'],
            ['dojo/dom-class', 'domClass'],
            ['dojo/dom-attr', 'domAttr'],
            ['esri/core/watchUtils', 'watchUtils'],
        ];
        if (version) {
            let exclusionList = [];
            switch(version) {
                case "4.10":
                    exclusionList = ['esri/symbols/CIMSymbol'];
                    break;
            }
            modules = modules.filter(function(i){ return exclusionList.indexOf(i[0]) < 0; });
        }    
        const result = await esriLoader
            .loadModules(modules.map(x => x[0]))
            .catch(() => Promise.reject());
        modules.forEach((x, i) => {
            this.modules[x[1]] = result[i];
        });
        if (options) {
            const {
                center,
                scrollWheel,
                layers,
                basemap1Id,
                basemap2Id,
                canToggleBasemap,
                canOpenFullscreenMap,
                zoom,
                disableDefaultUI,
                showPopup,
                focusOnPopup,
                canZoomToPopup,
                tooltipDelay,
                mouseEventHandlers,
                showSelectionToolbar,
                selectionCreatedHandler
            } = options;

            this.scrollWheel = scrollWheel == null ? this.scrollWheel : scrollWheel;
            this.layers = layers && Array.isArray(layers) ? layers : this.layers;
            this.basemap1 = typeof basemap1Id === 'string' ? basemap1Id : this.basemap1;
            this.basemap2 = typeof basemap2Id === 'string' ? basemap2Id : this.basemap2;
            this.canToggleBasemap =
                canToggleBasemap == null ? this.canToggleBasemap : canToggleBasemap;
            this.canOpenFullscreenMap =
                canOpenFullscreenMap == null ? this.canOpenFullscreenMap : canOpenFullscreenMap;
            this.zoom = zoom >= 0 ? zoom : this.zoom;
            this.latitude = center && center.latitude ? center.latitude : this.latitude;
            this.longitude = center && center.longitude ? center.longitude : this.longitude;
            this.disableDefaultUI =
                disableDefaultUI == null ? this.disableDefaultUI : disableDefaultUI;
            this.showPopup = showPopup == null ? this.showPopup : showPopup;
            this.focusOnPopup = focusOnPopup == null ? this.focusOnPopup : focusOnPopup;
            this.canZoomToPopup = canZoomToPopup == null ? this.canZoomToPopup : canZoomToPopup;
            this.tooltipDelay = tooltipDelay ? tooltipDelay : this.tooltipDelay;
            this.mouseEventHandlers =
                mouseEventHandlers && toString.call(mouseEventHandlers) === '[object Object]'
                    ? mouseEventHandlers
                    : {};
            this.showSelectionToolbar =
                showSelectionToolbar == null ? this.showSelectionToolbar : showSelectionToolbar;
            this.selectionCreatedHandler = selectionCreatedHandler;
        }

        const layers = this.layers.map(
            ({ id = 'layer', type = 'GraphicsLayer', featureLayerOptions = {} }) => {
                return type === 'FeatureLayer'
                    ? this.createFeatureLayer(id, featureLayerOptions)
                    : this.createGraphicsLayer(id);
            }
        );

        this.map = new this.modules.Map({
            basemap: this.basemap1,
            layers
        });

        this.view = new this.modules.MapView({
            container: mapDivId,
            map: this.map,
            zoom: this.zoom,
            center: [this.longitude, this.latitude],
            popup: {
                featureNavigationEnabled: true
            },
            constraints: {
                snapToZoom: false
            }
        });

        this.originScreenPoint = Object.freeze({
            x: 0,
            y: 0,
            spatialReference: Object.assign({}, this.view.spatialReference)
        });

        if (this.showPopup && !this.canZoomToPopup) {
            this.view.popup.actions = [];
        }

        return this.view.when(
            () => {
                // map view has finished loading...
                this.isViewInitialized = true;

                if (this.showPopup === false) {
                    this.view.popup = null;
                }

                this._setupMapViewUI();
                this._setupTooltip();
                this._setupEventHandlers();

                return this.isViewInitialized;
            },
            () => Promise.reject()
        );
    }

    setLayerVisibility(layerId, visible) {
        this.map.findLayerById(layerId).visible = visible;
    }

    /**
     * add a div to mimic browser's default tooltip for map features
     */
    _setupTooltip() {
        const { left, top } = $(this.view.container).position();
        this.popupContainerOffset = { x: 0, y: 0 };
        this.popupContainer = $(
            `<div class="map-tooltips" style="top: ${top}px; left: ${left}px">`
        ).appendTo(this.view.container);

        this.tooltip = this.modules.domConstruct.create(
            'div',
            { class: 'bootstrap map-tooltip hide', style: 'z-index: 1' },
            this.popupContainer[0]
        );
    }

    /**
     * setup tools on the map view e.g. zoom in/out buttons once the map view has finished loading
     */
    _setupMapViewUI() {
        if (this.disableDefaultUI) {
            this.view.ui.empty('top-left');
            this.view.ui.empty('top-right');
            this.view.ui.empty('bottom-left');
            this.view.ui.empty('bottom-right');
            return;
        }

        if (this.canToggleBasemap) {
            const basemapToggle = new this.modules.BasemapToggle({
                view: this.view,
                nextBasemap: this.basemap2
            });

            this.view.ui.add(basemapToggle, 'top-left');
        }

        if (this.canOpenFullscreenMap) {
            const fullscreen = new this.modules.Fullscreen({
                view: this.view
            });
            this.view.ui.add(fullscreen, 'top-right');
        }

        this.view.ui.move(['zoom'], 'bottom-right');

        if (this.showSelectionToolbar) {
            this.addGraphicsLayer(this.selectionLayerId, 0);
            this.selectionLayer = this.map.findLayerById(this.selectionLayerId);

            this.sketch = new this.modules.Sketch({
                layer: this.selectionLayer,
                view: this.view,
                availableCreateTools: ['polygon', 'rectangle', 'circle'],
                creationMode: 'single'
            });
            this.sketch.viewModel.updateOnGraphicClick = false;
            this.view.ui.add(this.sketch, 'top-right');
        }
    }

    _isExistingFeatureOrGraphicsLayer(id) {
        const layer = this.map.findLayerById(id);
        return layer && (layer.type === 'feature' || layer.type === 'graphics');
    }

    _setupEventHandlers() {
        let alreadyFilteredFeatures = false;
        if (this.showPopup && this.focusOnPopup) {
            // Re-center map on popup

            this.view.popup.watch(['features'], (newPopupFeatures, oldPopupFeatures, propName) => {
                if (alreadyFilteredFeatures) {
                    if (newPopupFeatures.length === 0) {
                        // somehow the API still shows an empty popup when there's no visible feature.
                        // we'll close the popup explicitly
                        this.view.popup.close();
                    }
                    alreadyFilteredFeatures = false;
                    return;
                }

                if (this.view.popup.features.length > 0) {
                    const visibleFeatures = this.view.popup.features.filter(f => f.visible);
                    if (visibleFeatures.length < this.view.popup.features.length) {
                        // setting this.view.popup.features will trigger this.view.popup.watch(['features']) again
                        // therefore we use the alreadyFilteredFeatures flag to indicate there's no need to go through this part again
                        this.view.popup.features = visibleFeatures;
                        alreadyFilteredFeatures = true;
                    }

                    if (visibleFeatures.length > 0) {
                        this.view.goTo(this.view.popup.location);
                        this.modules.domClass.add(this.tooltip, 'hide');
                    }
                }
            });

            this.view.watch(['zoom'], (newVal, oldVal, propName) => {
                const { features } = this.view.popup;
                if (Math.abs(oldVal - newVal) < 0.1 || !features || features.length === 0) {
                    return;
                }

                const geometry = features[0].geometry;
                this.view.popup.location = geometry.extent ? geometry.extent.center : geometry;
            });
        }

        if (!this.scrollWheel) {
            this.view.on('mouse-wheel', evt => evt.stopPropagation());
        }

        this.view.on('pointer-move', async evt => {
            const result = await this.view.hitTest(evt);
            if (
                result.results.length === 0 ||
                !result.results[0].graphic ||
                !this._isExistingFeatureOrGraphicsLayer(
                    (result.results[0].graphic.layer || {}).id
                ) ||
                !(result.results[0].graphic.attributes || {}).tooltipText ||
                !result.results[0].graphic.visible
            ) {
                this.isTooltipVisible = false;
                this.modules.domClass.add(this.tooltip, 'hide');
                return;
            }

            const { x, y } = evt;
            const { x: offsetX, y: offsetY } = this.popupContainerOffset;
            this.modules.domAttr.set(
                this.tooltip,
                'innerHTML',
                result.results[0].graphic.attributes.tooltipText
            );
            this.modules.domAttr.set(this.tooltip, 'style', {
                left: `${x + 10 - offsetX}px`,
                top: `${y + 10 - offsetY}px`
            });
            this.modules.domClass.remove(this.tooltip, 'hide');
            this.isTooltipVisible = true;
            setTimeout(() => {
                if (this.isTooltipVisible) {
                    this.modules.domClass.remove(this.tooltip, 'hide');
                }
            }, this.tooltipDelay);
        });

        this.view.on('click', async evt => {
            const result = await this.view.hitTest(evt);
            const activeGraphics = [];
            result.results.forEach((item) => {
                if (
                    item.graphic &&
                    item.graphic.layer &&
                    item.graphic.visible &&
                    this._isExistingFeatureOrGraphicsLayer(item.graphic.layer.id)
                ) {
                    activeGraphics.push(item.graphic);
                }
            });
            const graphic = activeGraphics[0];

            const { leftClickHandler, rightClickHandler } = this.mouseEventHandlers;

            if (evt.button === MOUSE_BUTTON.LEFT) {
                if (graphic && graphic.layer.id === this.selectionLayerId) {
                    this.sketch.viewModel.update(graphic);
                } else if (!(graphic && graphic.uid)) {
                    this.sketch && this.sketch.cancel();
                }

                if (leftClickHandler) {
                    leftClickHandler();
                }
            }

            if (evt.button === MOUSE_BUTTON.RIGHT && graphic && rightClickHandler) {
                const mousePosition = {
                    top: evt.native.clientY,
                    left: evt.native.clientX
                };
                rightClickHandler(mousePosition, graphic.layer.id, graphic.attributes, activeGraphics);
            }
        });

        // Watch `interacting` instead of `stationary` to start panning the
        // custom popups because it doesn't fire when scale changes (we handle
        // zooming differently).
        this.modules.watchUtils.watch(this.view, 'interacting', () => {
            if (this.view.interacting) {
                // Start moving the custom popups with the map.
                this.panCustomPopups();
            }
        });

        let didScaleChange = false;

        this.modules.watchUtils.watch(this.view, 'scale', () => {
            if (!didScaleChange) {
                this.hideCustomPopups();
                didScaleChange = true;
            }
        });

        this.modules.watchUtils.watch(this.view, 'stationary', () => {
            if (!this.isViewInitialized) {
                return;
            }

            if (!this.view.stationary) {
                this.mouseEventHandlers.leftClickHandler &&
                    this.mouseEventHandlers.leftClickHandler();
                return;
            }

            if (didScaleChange) {
                this.repositionCustomPopups();
                didScaleChange = false;
            }
        });

        if (this.showSelectionToolbar) {
            this._setupSketchEvents();
        }
    }

    _setupSketchEvents() {
        this.sketch.on('create', event => {
            if (event.state === 'complete') {
                this.selectionId += 1;
                event.graphic.attributes = {
                    id: this.selectionId
                };
                this.selectionCreatedHandler(event.graphic, true);
            }
        });

        this.sketch.on('update', event => {
            this.selectionCreatedHandler(
                event.graphics[0],
                ['complete', 'cancel'].includes(event.state)
            );
        });

        this.sketch.on('delete', event => {
            event.graphics.forEach(graphic => this.removeCustomPopup(graphic));
        });
    }

    /**
     * set the extent of the map view using an array of `latitude/longitude` key/value pairs
     */
    setExtent(points, expandExtent = true) {
        if (!points || points.length === 0 || !Array.isArray(points)) {
            return;
        }

        const validPoints = points.filter(point => point.latitude && point.longitude);
        if (!validPoints.length) {
            return;
        }

        const ring = validPoints.reduce((points, point) => {
            points.push([point.longitude, point.latitude]);
            return points;
        }, []);

        if (ring.length === 1) {
            const lon = ring[0][0];
            const lat = ring[0][1];
            const offset = 0.1;
            ring.push([lon - offset, lat - offset]);
            ring.push([lon + offset, lat + offset]);
        }

        const lastPoint = validPoints[0];
        ring.push([lastPoint.longitude, lastPoint.latitude]);

        const tempPolygon = new this.modules.Polygon({
            rings: [ring]
        });

        this.view.extent = tempPolygon.extent.expand(expandExtent ? 1.8 : 1);
    }

    /**
     * set the extent of the map view using an array of `x/y` (web mercator coordinates) key/value pairs
     */
    setExtentWebMercator(points, expandExtent = true) {
        const pointsInLngLat = [];
        points.forEach(pointInXY => {
            if (!pointInXY || !pointInXY.x || !pointInXY.y) {
                return;
            }

            const pointInLngLat = this.modules.webMercatorUtils.xyToLngLat(
                pointInXY.x,
                pointInXY.y
            );
            pointsInLngLat.push({
                longitude: pointInLngLat[0],
                latitude: pointInLngLat[1]
            });
        });
        this.setExtent(pointsInLngLat, expandExtent);
    }

    getExtent() {
        return this.view.extent;
    }

    getZoom() {
        return this.view.zoom;
    }

    setZoom(scale = 13) {
        this.view.zoom = scale;
    }

    center() {
        this.centerAt(this.view.extent.center);
    }

    centerAt(center) {
        this.view.center = center;
    }

    clearSketchingSelections() {
        this.sketch.cancel();

        this.removeCustomPopups();

        const selectionLayerId = this.selectionLayer.id;
        this.graphicsLayerRemoveAllGraphics(selectionLayerId);

        this.selectionId = 0;
    }

    removeCustomUI() {
        // hide the tooltip of technicians/jobs/appointments
        this.tooltip && this.modules.domClass.add(this.tooltip, 'hide');

        // clear the selections popups
        this.sketch && this.clearSketchingSelections();
    }
}

window.MapperEsri = MapperEsri;
